import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  UserPlus,
  Trash2,
  KeyRound,
  ShieldCheck,
  Loader2,
  Clock3,
  CalendarDays,
  Settings2,
  Plus,
  Pencil,
  Moon,
  SunMedium,
} from "lucide-react";
import { http, apiError } from "@/lib/api";
import AdminShell, {
  PageHead,
  useAdmin,
} from "@/components/AdminShell";
import {
  Toggle,
  Field,
  Modal,
} from "@/pages/admin/Products";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function shiftOvernight(shift) {
  if (!shift?.start_time || !shift?.end_time) return false;
  return shift.end_time <= shift.start_time;
}

function prettyDate(value) {
  if (!value) return "-";
  const [y, m, d] = String(value).split("-");
  if (!y || !m || !d) return value;
  return `${d}-${m}-${y}`;
}

function EmployeesInner() {
  const admin = useAdmin();
  const isOwner = admin.user?.role === "owner";

  const [users, setUsers] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [attendanceConfig, setAttendanceConfig] = useState({
    late_tolerance_minutes: 0,
  });

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [resetFor, setResetFor] = useState(null);
  const [newPass, setNewPass] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [shiftManagerOpen, setShiftManagerOpen] = useState(false);
  const [shiftFormOpen, setShiftFormOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [shiftBusy, setShiftBusy] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    name: "",
    start_time: "08:00",
    end_time: "16:00",
  });
  const [shiftDeleteTarget, setShiftDeleteTarget] = useState(null);

  const [scheduleFor, setScheduleFor] = useState(null);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleDeleteTarget, setScheduleDeleteTarget] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    shift_id: "",
    start_date: "",
    end_date: "",
  });

  async function load() {
    try {
      const [usersRes, shiftsRes, configRes] = await Promise.all([
        http.get("/admin/users"),
        http.get("/admin/work-shifts"),
        http.get("/admin/attendance/config"),
      ]);

      setUsers(usersRes.data);
      setShifts(shiftsRes.data || []);
      setAttendanceConfig(
        configRes.data || {
          late_tolerance_minutes: 0,
        }
      );
    } catch (e) {
      toast.error(apiError(e));
      setUsers([]);
    }
  }

  useEffect(() => {
    if (isOwner) load();
  }, [isOwner]);

  const activeShifts = useMemo(
    () => shifts.filter((s) => s.active !== false),
    [shifts]
  );

  async function create() {
    try {
      await http.post("/admin/users", form);
      toast.success("Karyawan ditambahkan");
      setAdding(false);
      setForm({
        name: "",
        email: "",
        password: "",
      });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function toggleActive(u) {
    try {
      await http.patch(`/admin/users/${u.id}`, {
        active: !u.active,
      });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function resetPass() {
    if (newPass.length < 6) {
      return toast.error("Password minimal 6 karakter");
    }

    try {
      await http.patch(`/admin/users/${resetFor.id}`, {
        password: newPass,
      });
      toast.success("Password diubah");
      setResetFor(null);
      setNewPass("");
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;

    setDeleteBusy(true);

    try {
      await http.delete(`/admin/users/${deleteTarget.id}`);
      toast.success("Karyawan dihapus");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  function openNewShift() {
    setEditingShift(null);
    setShiftForm({
      name: `Shift ${shifts.length + 1}`,
      start_time: "08:00",
      end_time: "16:00",
    });
    setShiftFormOpen(true);
  }

  function openEditShift(shift) {
    setEditingShift(shift);
    setShiftForm({
      name: shift.name || "",
      start_time: shift.start_time || "08:00",
      end_time: shift.end_time || "16:00",
    });
    setShiftFormOpen(true);
  }

  async function saveShift() {
    const name = String(shiftForm.name || "").trim();
    const start = String(shiftForm.start_time || "").trim();
    const end = String(shiftForm.end_time || "").trim();

    if (!name) {
      return toast.error("Nama shift wajib diisi");
    }

    if (!TIME_RE.test(start)) {
      return toast.error("Jam masuk harus format 24 jam HH:MM");
    }

    if (!TIME_RE.test(end)) {
      return toast.error("Jam pulang harus format 24 jam HH:MM");
    }

    if (start === end) {
      return toast.error("Jam masuk dan pulang tidak boleh sama");
    }

    setShiftBusy(true);

    try {
      if (editingShift) {
        await http.patch(
          `/admin/work-shifts/${editingShift.id}`,
          {
            name,
            start_time: start,
            end_time: end,
          }
        );
        toast.success("Shift diperbarui");
      } else {
        await http.post("/admin/work-shifts", {
          name,
          start_time: start,
          end_time: end,
        });
        toast.success("Shift ditambahkan");
      }

      setShiftFormOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setShiftBusy(false);
    }
  }

  async function confirmDeleteShift() {
    if (!shiftDeleteTarget) return;

    setDeleteBusy(true);

    try {
      await http.delete(
        `/admin/work-shifts/${shiftDeleteTarget.id}`
      );
      toast.success("Shift dihapus");
      setShiftDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  async function saveTolerance() {
    try {
      const value = Math.max(
        0,
        Math.min(
          180,
          Number(
            attendanceConfig.late_tolerance_minutes || 0
          )
        )
      );

      await http.patch("/admin/attendance/config", {
        late_tolerance_minutes: value,
      });

      toast.success("Toleransi terlambat disimpan");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  function openSchedule(u, schedule = null) {
    setScheduleFor(u);
    setEditingSchedule(schedule);

    setScheduleForm({
      shift_id:
        schedule?.shift_id ||
        activeShifts[0]?.id ||
        "",
      start_date: schedule?.start_date || "",
      end_date: schedule?.end_date || "",
    });
  }

  async function saveSchedule() {
    if (!scheduleFor) return;

    if (!scheduleForm.shift_id) {
      return toast.error("Pilih shift terlebih dahulu");
    }

    if (
      !scheduleForm.start_date ||
      !scheduleForm.end_date
    ) {
      return toast.error(
        "Tanggal mulai dan selesai wajib dipilih"
      );
    }

    if (
      scheduleForm.end_date <
      scheduleForm.start_date
    ) {
      return toast.error(
        "Tanggal selesai tidak boleh sebelum tanggal mulai"
      );
    }

    setScheduleBusy(true);

    try {
      if (editingSchedule) {
        await http.patch(
          `/admin/users/${scheduleFor.id}/schedules/${editingSchedule.id}`,
          scheduleForm
        );
        toast.success("Jadwal karyawan diperbarui");
      } else {
        await http.post(
          `/admin/users/${scheduleFor.id}/schedules`,
          scheduleForm
        );
        toast.success("Jadwal karyawan ditambahkan");
      }

      setScheduleFor(null);
      setEditingSchedule(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setScheduleBusy(false);
    }
  }

  async function confirmDeleteSchedule() {
    if (
      !scheduleDeleteTarget?.employee ||
      !scheduleDeleteTarget?.schedule
    ) {
      return;
    }

    setDeleteBusy(true);

    try {
      await http.delete(
        `/admin/users/${scheduleDeleteTarget.employee.id}/schedules/${scheduleDeleteTarget.schedule.id}`
      );
      toast.success("Jadwal dihapus");
      setScheduleDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  if (!isOwner) {
    return (
      <div className="rounded-3xl border border-dashed border-border p-12 text-center text-muted-foreground">
        <ShieldCheck
          className="mx-auto mb-3"
          size={32}
        />
        <p className="font-heading text-lg">
          Halaman khusus pemilik.
        </p>
      </div>
    );
  }

  if (users === null) {
    return (
      <div className="flex justify-center pt-20">
        <Loader2 className="wm-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <PageHead
        eyebrow="Tim"
        title="Karyawan"
        desc="Kelola karyawan, shift kerja, dan periode jadwal."
      >
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShiftManagerOpen(true)}
            className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-bold text-card-foreground"
          >
            <Settings2 size={16} />
            Kelola Shift
          </button>

          <button
            data-testid="add-employee-btn"
            onClick={() => setAdding(true)}
            className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground transition-transform active:scale-95"
          >
            <UserPlus size={16} />
            Karyawan
          </button>
        </div>
      </PageHead>

      <div className="mb-4 rounded-2xl border border-border bg-card p-4">
        <div className="flex items-start gap-3">
          <Clock3
            className="mt-0.5 text-primary"
            size={18}
          />
          <div>
            <div className="text-sm font-bold text-card-foreground">
              Toleransi terlambat global:{" "}
              {Number(
                attendanceConfig.late_tolerance_minutes || 0
              )}{" "}
              menit
            </div>

            <div className="mt-1 text-xs text-muted-foreground">
              Berlaku untuk semua karyawan dan semua shift.
              Pengaturannya ada di tombol Kelola Shift.
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {users.map((u) => (
          <div
            key={u.id}
            data-testid={`user-${u.id}`}
            className="rounded-3xl border border-border bg-card p-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className={`grid h-11 w-11 flex-none place-items-center rounded-xl font-heading text-lg font-black ${
                    u.role === "owner"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-card-foreground"
                  }`}
                >
                  {u.name?.[0]?.toUpperCase() || "?"}
                </div>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 font-heading text-sm font-bold text-card-foreground">
                    <span>{u.name}</span>

                    {u.role === "owner" && (
                      <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-black uppercase text-primary">
                        Pemilik
                      </span>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground">
                    {u.email}
                  </div>
                </div>
              </div>

              {u.role === "owner" ? (
                <span className="text-xs font-semibold text-muted-foreground">
                  Akun utama
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <Toggle
                    label=""
                    checked={u.active}
                    onChange={() => toggleActive(u)}
                  />

                  <button
                    type="button"
                    onClick={() => openSchedule(u)}
                    className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary"
                    title="Tambah jadwal kerja"
                  >
                    <CalendarDays size={15} />
                  </button>

                  <button
                    onClick={() => setResetFor(u)}
                    className="grid h-9 w-9 place-items-center rounded-xl bg-muted"
                    title="Ubah sandi"
                  >
                    <KeyRound size={15} />
                  </button>

                  <button
                    onClick={() => setDeleteTarget(u)}
                    className="grid h-9 w-9 place-items-center rounded-xl bg-destructive/10 text-destructive"
                    title="Hapus"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              )}
            </div>

            {u.role !== "owner" && (
              <div className="mt-4 border-t border-border pt-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="text-[11px] font-black uppercase tracking-[0.15em] text-muted-foreground">
                    Jadwal Kerja
                  </div>

                  <button
                    type="button"
                    onClick={() => openSchedule(u)}
                    className="flex items-center gap-1 rounded-lg bg-primary/10 px-2.5 py-1.5 text-[11px] font-bold text-primary"
                  >
                    <Plus size={13} />
                    Jadwal
                  </button>
                </div>

                {u.schedules?.length ? (
                  <div className="space-y-2">
                    {u.schedules.map((schedule) => (
                      <div
                        key={schedule.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-muted/45 p-3"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-bold text-card-foreground">
                              {schedule.shift_name}
                            </span>

                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                              {schedule.start_time} → {schedule.end_time}
                            </span>

                            {schedule.overnight && (
                              <span className="flex items-center gap-1 rounded-full bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold text-indigo-400">
                                <Moon size={10} />
                                Lintas Hari
                              </span>
                            )}
                          </div>

                          <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                            <CalendarDays size={12} />
                            {prettyDate(schedule.start_date)}
                            <span>—</span>
                            {prettyDate(schedule.end_date)}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() =>
                              openSchedule(u, schedule)
                            }
                            className="grid h-8 w-8 place-items-center rounded-lg bg-card text-card-foreground"
                            title="Edit jadwal"
                          >
                            <Pencil size={13} />
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              setScheduleDeleteTarget({
                                employee: u,
                                schedule,
                              })
                            }
                            className="grid h-8 w-8 place-items-center rounded-lg bg-destructive/10 text-destructive"
                            title="Hapus jadwal"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                    Belum ada jadwal shift.
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {shiftManagerOpen && (
        <Modal
          onClose={() => setShiftManagerOpen(false)}
          title="Kelola Shift Kerja"
        >
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-muted/35 p-4">
              <Field label="Toleransi Terlambat Global (menit)">
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    max="180"
                    value={
                      attendanceConfig.late_tolerance_minutes
                    }
                    onChange={(e) =>
                      setAttendanceConfig((p) => ({
                        ...p,
                        late_tolerance_minutes:
                          e.target.value,
                      }))
                    }
                    className="input"
                  />

                  <button
                    type="button"
                    onClick={saveTolerance}
                    className="rounded-xl bg-primary px-4 text-sm font-bold text-primary-foreground"
                  >
                    Simpan
                  </button>
                </div>
              </Field>

              <div className="mt-2 text-xs leading-5 text-muted-foreground">
                Hanya diatur sekali dan otomatis berlaku untuk
                semua shift. Datang lebih awal tidak pernah
                dianggap telat.
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading text-base font-bold text-card-foreground">
                  Daftar Shift
                </div>
                <div className="text-xs text-muted-foreground">
                  Jam menggunakan format 24 jam.
                </div>
              </div>

              <button
                type="button"
                onClick={openNewShift}
                className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-xs font-bold text-primary-foreground"
              >
                <Plus size={14} />
                Tambah Shift
              </button>
            </div>

            <div className="space-y-2">
              {shifts.map((shift) => (
                <div
                  key={shift.id}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-border p-3"
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-bold text-card-foreground">
                        {shift.name}
                      </span>

                      {shiftOvernight(shift) && (
                        <span className="flex items-center gap-1 rounded-full bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold text-indigo-400">
                          <Moon size={10} />
                          Lintas Hari
                        </span>
                      )}
                    </div>

                    <div className="mt-1 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                      <span>{shift.start_time}</span>
                      <span>→</span>
                      <span>{shift.end_time}</span>

                      {shiftOvernight(shift) && (
                        <span className="font-normal">
                          (pulang hari berikutnya)
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => openEditShift(shift)}
                      className="grid h-8 w-8 place-items-center rounded-lg bg-muted"
                      title="Edit shift"
                    >
                      <Pencil size={13} />
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setShiftDeleteTarget(shift)
                      }
                      className="grid h-8 w-8 place-items-center rounded-lg bg-destructive/10 text-destructive"
                      title="Hapus shift"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}

      {shiftFormOpen && (
        <Modal
          onClose={() =>
            !shiftBusy &&
            setShiftFormOpen(false)
          }
          title={
            editingShift
              ? "Edit Shift"
              : "Tambah Shift"
          }
        >
          <div className="space-y-4">
            <Field label="Nama Shift">
              <input
                value={shiftForm.name}
                onChange={(e) =>
                  setShiftForm((p) => ({
                    ...p,
                    name: e.target.value,
                  }))
                }
                placeholder="Shift 1"
                className="input"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Jam Masuk">
                <input
                  type="time"
                  step="60"
                  value={shiftForm.start_time}
                  onChange={(e) =>
                    setShiftForm((p) => ({
                      ...p,
                      start_time: e.target.value,
                    }))
                  }
                  className="input"
                />
              </Field>

              <Field label="Jam Pulang">
                <input
                  type="time"
                  step="60"
                  value={shiftForm.end_time}
                  onChange={(e) =>
                    setShiftForm((p) => ({
                      ...p,
                      end_time: e.target.value,
                    }))
                  }
                  className="input"
                />
              </Field>
            </div>

            {shiftForm.start_time &&
              shiftForm.end_time &&
              shiftForm.end_time <= shiftForm.start_time && (
                <div className="rounded-2xl border border-indigo-500/25 bg-indigo-500/10 p-3 text-xs leading-5 text-indigo-300">
                  <div className="flex items-center gap-2 font-bold">
                    <Moon size={14} />
                    Shift lintas hari
                  </div>

                  <div className="mt-1">
                    {shiftForm.start_time} →{" "}
                    {shiftForm.end_time} berarti masuk hari ini
                    dan pulang otomatis pada hari berikutnya.
                  </div>
                </div>
              )}

            <button
              type="button"
              onClick={saveShift}
              disabled={shiftBusy}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-bold text-primary-foreground disabled:opacity-50"
            >
              {shiftBusy ? (
                <Loader2
                  className="wm-spin"
                  size={16}
                />
              ) : (
                <Clock3 size={16} />
              )}
              Simpan Shift
            </button>
          </div>
        </Modal>
      )}

      {scheduleFor && (
        <Modal
          onClose={() =>
            !scheduleBusy &&
            setScheduleFor(null)
          }
          title={`${
            editingSchedule
              ? "Edit"
              : "Tambah"
          } Jadwal — ${scheduleFor.name}`}
        >
          <div className="space-y-4">
            <Field label="Pilih Shift">
              <select
                value={scheduleForm.shift_id}
                onChange={(e) =>
                  setScheduleForm((p) => ({
                    ...p,
                    shift_id: e.target.value,
                  }))
                }
                className="input"
              >
                <option value="">
                  Pilih shift
                </option>

                {activeShifts.map((shift) => (
                  <option
                    key={shift.id}
                    value={shift.id}
                  >
                    {shift.name} —{" "}
                    {shift.start_time} s/d{" "}
                    {shift.end_time}
                    {shiftOvernight(shift)
                      ? " (lintas hari)"
                      : ""}
                  </option>
                ))}
              </select>
            </Field>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Berlaku Mulai">
                <input
                  type="date"
                  value={
                    scheduleForm.start_date
                  }
                  onChange={(e) =>
                    setScheduleForm((p) => ({
                      ...p,
                      start_date:
                        e.target.value,
                    }))
                  }
                  className="input"
                />
              </Field>

              <Field label="Berlaku Sampai">
                <input
                  type="date"
                  value={
                    scheduleForm.end_date
                  }
                  min={
                    scheduleForm.start_date ||
                    undefined
                  }
                  onChange={(e) =>
                    setScheduleForm((p) => ({
                      ...p,
                      end_date:
                        e.target.value,
                    }))
                  }
                  className="input"
                />
              </Field>
            </div>

            {(() => {
              const chosen = activeShifts.find(
                (shift) =>
                  shift.id ===
                  scheduleForm.shift_id
              );

              if (!chosen) return null;

              return (
                <div className="rounded-2xl border border-border bg-muted/35 p-3 text-xs leading-5 text-muted-foreground">
                  <div className="flex items-center gap-2 font-bold text-card-foreground">
                    {shiftOvernight(chosen) ? (
                      <Moon size={14} />
                    ) : (
                      <SunMedium size={14} />
                    )}
                    {chosen.name}:{" "}
                    {chosen.start_time} →{" "}
                    {chosen.end_time}
                  </div>

                  {shiftOvernight(chosen) && (
                    <div className="mt-1">
                      Jam pulang otomatis dianggap hari
                      berikutnya. Contoh jadwal tanggal 10:
                      masuk tanggal 10 pukul{" "}
                      {chosen.start_time}, pulang tanggal 11
                      pukul {chosen.end_time}.
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="rounded-2xl bg-muted/50 p-3 text-xs leading-5 text-muted-foreground">
              Tanggal dipilih melalui kalender. Jadwal berlaku
              setiap hari selama periode tersebut tanpa perlu
              diatur satu per satu.
            </div>

            <button
              type="button"
              onClick={saveSchedule}
              disabled={scheduleBusy}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-bold text-primary-foreground disabled:opacity-50"
            >
              {scheduleBusy ? (
                <Loader2
                  className="wm-spin"
                  size={16}
                />
              ) : (
                <CalendarDays size={16} />
              )}
              Simpan Jadwal
            </button>
          </div>
        </Modal>
      )}

      {adding && (
        <Modal
          onClose={() => setAdding(false)}
          title="Karyawan Baru"
        >
          <div className="space-y-3">
            <Field label="Nama">
              <input
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    name: e.target.value,
                  }))
                }
                className="input"
              />
            </Field>

            <Field label="Email">
              <input
                type="email"
                value={form.email}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    email: e.target.value,
                  }))
                }
                className="input"
              />
            </Field>

            <Field label="Password (min 6)">
              <input
                type="text"
                value={form.password}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    password: e.target.value,
                  }))
                }
                className="input"
              />
            </Field>

            <button
              onClick={create}
              className="w-full rounded-xl bg-primary py-3 font-bold text-primary-foreground"
            >
              Buat Akun
            </button>
          </div>
        </Modal>
      )}

      {resetFor && (
        <Modal
          onClose={() => setResetFor(null)}
          title={`Ubah Sandi — ${resetFor.name}`}
        >
          <div className="space-y-3">
            <Field label="Password Baru (min 6)">
              <input
                type="text"
                value={newPass}
                onChange={(e) =>
                  setNewPass(e.target.value)
                }
                className="input"
              />
            </Field>

            <button
              onClick={resetPass}
              className="w-full rounded-xl bg-primary py-3 font-bold text-primary-foreground"
            >
              Simpan Sandi
            </button>
          </div>
        </Modal>
      )}

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        title="Hapus Karyawan?"
        message="Akun dan jadwal kerja karyawan akan dihapus."
        itemName={deleteTarget?.name || ""}
        busy={deleteBusy}
        onCancel={() =>
          !deleteBusy &&
          setDeleteTarget(null)
        }
        onConfirm={confirmDelete}
      />

      <ConfirmDeleteModal
        open={Boolean(shiftDeleteTarget)}
        title="Hapus Shift?"
        message="Shift hanya dapat dihapus jika sudah tidak dipakai pada jadwal karyawan."
        itemName={
          shiftDeleteTarget?.name || ""
        }
        busy={deleteBusy}
        onCancel={() =>
          !deleteBusy &&
          setShiftDeleteTarget(null)
        }
        onConfirm={confirmDeleteShift}
      />

      <ConfirmDeleteModal
        open={Boolean(scheduleDeleteTarget)}
        title="Hapus Jadwal?"
        message="Periode jadwal kerja ini akan dihapus."
        itemName={
          scheduleDeleteTarget
            ? `${
                scheduleDeleteTarget
                  .schedule.shift_name
              } • ${prettyDate(
                scheduleDeleteTarget
                  .schedule.start_date
              )} - ${prettyDate(
                scheduleDeleteTarget
                  .schedule.end_date
              )}`
            : ""
        }
        busy={deleteBusy}
        onCancel={() =>
          !deleteBusy &&
          setScheduleDeleteTarget(null)
        }
        onConfirm={confirmDeleteSchedule}
      />

      <style>{`
        .input{
          width:100%;
          border:1px solid hsl(var(--border));
          background:hsl(var(--muted));
          border-radius:.75rem;
          padding:.65rem .8rem;
          font-size:.85rem;
          outline:none;
          color:hsl(var(--card-foreground));
        }
      `}</style>
    </>
  );
}

export default function Employees() {
  return (
    <AdminShell>
      <EmployeesInner />
    </AdminShell>
  );
}
